<script lang="ts">
  import { apiClient } from '@/utils/api';
  import VerifiedBadge from './VerifiedBadge.svelte';

  /**
   * 추천 인물 블록 (2026-08-17 시안 C 확정)
   *   - 3명만 노출 + "모두 보기". 피드에 끼어드는 블록이라 목록을 늘어놓지 않는다.
   *   - 추천 이유를 한 줄로 밝힌다 — 신뢰의 근거.
   *
   * 🔴 문구는 여기서 조립한다. 서버는 reason_code 와 수치만 준다 —
   *    완성 문구를 서버에 두면 iOS·Android·웹이 같은 문장에 묶이고 다국어에서 막힌다.
   */
  type Person = {
    id: string;
    username: string;
    name: string;
    avatarUrl?: string | null;
    position?: string | null;
    organizationName?: string | null;
    reputationScore: number;
    platformVerified: boolean;
    companyVerified: boolean;
    recentAnswerCount: number;
    recentPostCount: number;
    reasonCode: 'verified' | 'active' | 'reputation';
  };

  let { limit = 3 }: { limit?: number } = $props();

  let people = $state<Person[]>([]);
  let loading = $state(true);
  let followingIds = $state(new Set<string>());
  let busyIds = $state(new Set<string>());
  /** 실패를 삼키면 "버튼이 안 먹는다"로 보인다 — 사용자에게 알린다. */
  let followError = $state<string | null>(null);

  async function load() {
    const res = await apiClient.get<{ users: Person[] }>('/api/v1/people/suggested', {
      params: { limit }
    });
    if (res.success) people = res.data?.users ?? [];
    loading = false;
  }
  load();

  /** 서버가 준 근거를 사람 말로 옮긴다. 수치가 0이면 그 문구를 쓰지 않는다. */
  function reasonText(p: Person): string {
    const activity: string[] = [];
    if (p.recentAnswerCount > 0) activity.push(`최근 30일 답변 ${p.recentAnswerCount}건`);
    if (p.recentPostCount > 0) activity.push(`글 ${p.recentPostCount}건`);

    if (p.reasonCode === 'verified') {
      return ['운영진 검증', ...activity].join(' · ');
    }
    if (p.reasonCode === 'active' && activity.length > 0) {
      return activity.join(' · ');
    }
    // reputation — 활동 수치가 없을 때만 평판으로 말한다
    return activity.length > 0 ? activity.join(' · ') : `평판 ${p.reputationScore}`;
  }

  async function toggleFollow(userId: string) {
    if (busyIds.has(userId)) return;
    busyIds.add(userId);
    busyIds = new Set(busyIds);
    try {
      // 계약: 생성·삭제 모두 **대상 사용자 id** (follows_controller 의 create/destroy)
      followError = null;
      if (followingIds.has(userId)) {
        const r = await apiClient.delete(`/api/v1/follows/${userId}`);
        if (r.success) {
          followingIds.delete(userId);
          followingIds = new Set(followingIds);
        } else {
          followError = r.message ?? '팔로우를 해제하지 못했습니다. 잠시 후 다시 시도해 주세요.';
        }
      } else {
        const r = await apiClient.post('/api/v1/follows', { userId });
        if (r.success) {
          followingIds.add(userId);
          followingIds = new Set(followingIds);
        } else {
          followError = r.message ?? '팔로우하지 못했습니다. 잠시 후 다시 시도해 주세요.';
        }
      }
    } finally {
      busyIds.delete(userId);
      busyIds = new Set(busyIds);
    }
  }

  function initial(name: string) {
    return name?.[0] ?? '?';
  }
</script>

{#if loading}
  <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-secondary">
    <div class="h-4 w-24 animate-pulse rounded bg-gray-100 dark:bg-dark-tertiary"></div>
  </div>
{:else if people.length > 0}
  <section
    class="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-secondary"
    aria-label="추천 인물"
  >
    <div class="mb-2 flex items-center justify-between">
      <h2 class="text-[13px] font-bold text-gray-900 dark:text-dark-text-high">추천 인물</h2>
      <a href="/people" class="text-xs text-primary hover:underline">모두 보기</a>
    </div>

    {#if followError}
      <p class="mb-2 rounded-lg bg-error-light px-3 py-2 text-[11px] text-error dark:bg-error/10" role="alert">
        {followError}
      </p>
    {/if}

    <ul class="divide-y divide-gray-200 dark:divide-dark-border">
      {#each people as p (p.id)}
        <li class="flex items-center gap-3 py-2.5">
          <div
            class="grid h-9 w-9 flex-none place-items-center rounded-full bg-gray-200
                   text-xs font-bold text-gray-600 dark:bg-dark-tertiary dark:text-dark-text-medium"
          >
            {#if p.avatarUrl}
              <img src={p.avatarUrl} alt="" class="h-9 w-9 rounded-full object-cover" />
            {:else}
              {initial(p.name)}
            {/if}
          </div>

          <div class="min-w-0 flex-1">
            <a
              href={`/@${p.username}`}
              class="flex items-center gap-1 text-[13px] font-bold text-gray-900 hover:underline dark:text-dark-text-high"
            >
              <span class="truncate">{p.name}</span>
              {#if p.platformVerified}<VerifiedBadge kind="platform" />{/if}
              {#if p.companyVerified}<VerifiedBadge kind="company" />{/if}
            </a>
            {#if p.position || p.organizationName}
              <p class="truncate text-[11px] text-gray-500 dark:text-dark-text-low">
                {[p.organizationName, p.position].filter(Boolean).join(' · ')}
              </p>
            {/if}
            <p class="truncate text-[11px] text-gray-500 dark:text-dark-text-low">
              {reasonText(p)}
            </p>
          </div>

          <button
            type="button"
            onclick={() => toggleFollow(p.id)}
            disabled={busyIds.has(p.id)}
            class="flex-none rounded-lg px-3.5 py-1.5 text-xs font-bold transition disabled:opacity-50
                   {followingIds.has(p.id)
                     ? 'border border-gray-300 text-gray-600 dark:border-dark-border dark:text-dark-text-medium'
                     : 'bg-primary text-white hover:bg-primary-600'}"
          >
            {followingIds.has(p.id) ? '팔로잉' : '팔로우'}
          </button>
        </li>
      {/each}
    </ul>
  </section>
{/if}
